/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { matchSorter } from 'match-sorter';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import { OutlinedInput } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import OptionSelect from 'components/OptionSelect';
import { useTranslation } from 'utils/modifiedTranslation';
import { useQuestionnaireData } from '../../context';
import { GroupedOptionsList } from './GroupedOptionsList';
import { useStyles } from './optionsListWithSearchStyles';

const createFilterOptions = ({ isSearchableByOptionGroupName, onFilter }) => (options, { inputValue }) => {
  const preparedInputValue = inputValue.toLowerCase();
  const filtered = matchSorter(options, inputValue, {
    keys: [
      (item) => (isSearchableByOptionGroupName
        && preparedInputValue
        && item.groupLabel
        && item.groupLabel.toLowerCase().startsWith(preparedInputValue)
        ? inputValue
        : item.label.toLowerCase().includes(preparedInputValue) && item.label),
    ],
  });

  if (onFilter) {
    onFilter(inputValue, filtered);
  }

  return filtered;
};

const prepareOptions = ({
  options, groupedOptions, t, shouldSupplyWithGroupLabel,
}) => {
  const groupLabelByOptionValues = new Map();
  if (shouldSupplyWithGroupLabel) {
    groupedOptions.forEach(({ label: groupLabel, isDefault, options: groupOptions }) => {
      if (isDefault) {
        return;
      }
      const translatedGroupLabel = Array.isArray(groupLabel) ? groupLabel.map((l) => t(l)).join(' + ') : t(groupLabel);
      groupOptions.forEach(({ value }) => {
        groupLabelByOptionValues.set(value, translatedGroupLabel);
      });
    });
  }

  return options.map(({ label, value }) => ({
    label: Array.isArray(label) ? label.map((l) => t(l)).join(' + ') : t(label),
    value,
    groupLabel: groupLabelByOptionValues.get(value),
  }));
};

export const OptionsListWithSearch = ({
  id,
  fixedOptions,
  staticOptions = fixedOptions,
  options,
  groupedOptions,
  isMultipleSelection,
  searchPlaceholder,
  values,
  onChange,
  isSearchableByOptionGroupName,
  onSearchInputFocus,
  shouldScrollToTop = true,
  shouldShowSelectedGroup = true,
  hideNoKnownMutation = false,
  showSearchManager,
  SearchManagertIcon,
  SearchManagerComponent,
  className,
  customOptionClass,
  customCheckedOptionClass,
  customFixedOptionsClass,
  customFixedCheckedOptionsClass,
  searchFocusedClass,
  searchInputRootClass,
  searchInputNotchedOutlineClass,
  showCheckBoxIcon,
  scrollToOption,
  onSearchClick,
  onFilter,
  autoFocus = false,
}) => {
  const { appConfiguration: { providerSpecific } = {} } = useSelector((state) => state) ?? {};
  const classes = useStyles({ provider: providerSpecific?.code });
  const { t } = useTranslation();
  const { embedded } = useQuestionnaireData();
  const optionSelectContainerRef = useRef(null);
  const optionSelectRef = useRef();

  const preparedOptions = useMemo(
    () => prepareOptions({
      options,
      groupedOptions,
      t,
      shouldSupplyWithGroupLabel: isSearchableByOptionGroupName,
    }),
    [groupedOptions, isSearchableByOptionGroupName, options, t],
  );

  const {
    getClearProps,
    getInputProps,
    getListboxProps,
    groupedOptions: filteredOptions,
    setAnchorEl,
    inputValue,
  } = useAutocomplete({
    id: `search_${id}`,
    options: preparedOptions,
    clearOnEscape: true,
    filterOptions: createFilterOptions({ isSearchableByOptionGroupName, onFilter }),
  });

  const { onClick: clearSearchInput } = getClearProps();

  if (filteredOptions.length !== options.length) {
    filteredOptions.push(...staticOptions);
  }

  if (optionSelectContainerRef?.current && optionSelectRef?.current && scrollToOption) {
    const prevClientHeight = optionSelectRef.current.previousSibling?.clientHeight || 100;
    optionSelectContainerRef.current.scrollTop = (optionSelectRef?.current?.offsetTop ?? 0)
      - (optionSelectRef.current?.parentNode?.parentNode?.offsetTop ?? 0)
      - prevClientHeight;
  }

  const handleChange = useCallback(
    (...args) => {
      if (optionSelectContainerRef.current && shouldScrollToTop) {
        optionSelectContainerRef.current.scrollTop = 0;
      }
      clearSearchInput();
      onChange(...args);
    },
    [shouldScrollToTop, clearSearchInput, onChange],
  );

  const inputProps = getInputProps();

  const handleSearchInputFocus = (...args) => {
    inputProps.onFocus(...args);
    if (onSearchInputFocus) {
      onSearchInputFocus();
    }
  };

  const renderOptions = (
    <>
      {!!values.length && !inputProps.value && shouldShowSelectedGroup && (
        <GroupedOptionsList
          groupedOptions={[{ label: 'questionnaire.option_category.selected', options: values }]}
          values={values}
          onChange={onChange}
          isMultipleSelection={isMultipleSelection}
        />
      )}
      {!groupedOptions?.length || inputProps.value ? (
        <OptionSelect
          showCheckBoxIcon={showCheckBoxIcon}
          rootComponentProps={getListboxProps()}
          rootComponent="ul"
          listItemComponent="li"
          rootClassName={classes.list}
          customCheckedOptionClass={customCheckedOptionClass}
          customFixedCheckedOptionsClass={customFixedCheckedOptionsClass}
          customFixedOptionsClass={customFixedOptionsClass}
          customOptionClass={customOptionClass}
          isMultipleSelection={isMultipleSelection}
          options={inputProps.value ? filteredOptions : options}
          values={values}
          onChange={handleChange}
          inputValue={inputValue}
          small={embedded}
        />
      ) : (
        <GroupedOptionsList
          ref={optionSelectRef}
          scrollToOption={scrollToOption}
          customCheckedOptionClass={customCheckedOptionClass}
          customFixedCheckedOptionsClass={customFixedCheckedOptionsClass}
          customFixedOptionsClass={customFixedOptionsClass}
          customOptionClass={customOptionClass}
          showCheckBoxIcon={showCheckBoxIcon}
          groupedOptions={groupedOptions}
          values={values}
          onChange={handleChange}
          isMultipleSelection={isMultipleSelection}
        />
      )}
      {!hideNoKnownMutation && !inputProps.value && (
        <div>
          <OptionSelect
            customCheckedOptionClass={customCheckedOptionClass}
            customFixedCheckedOptionsClass={customFixedCheckedOptionsClass}
            customFixedOptionsClass={customFixedOptionsClass}
            customOptionClass={customOptionClass}
            rootComponent="ul"
            listItemComponent="li"
            options={fixedOptions}
            showCheckBoxIcon={showCheckBoxIcon}
            values={values}
            onChange={onChange}
            isMultipleSelection={isMultipleSelection}
            rootClassName={classes.fixedOptionsList}
          />
        </div>
      )}
    </>
  );

  const onSearchClickHandler = () => {
    if (onSearchClick) {
      onSearchClick();
    }
  };

  return (
    <div className={cx(classes.root, className)}>
      <div ref={setAnchorEl} className={classes.searchContainer}>
        <OutlinedInput
          fullWidth={!SearchManagertIcon}
          id={`search_${id}`}
          placeholder={t(searchPlaceholder)}
          classes={{
            root: searchInputRootClass || classes.searchInputRoot,
            input: classes.searchInput,
            notchedOutline: searchInputNotchedOutlineClass || classes.searchInputNotchedOutline,
            focused: searchFocusedClass || '',
          }}
          endAdornment={(
            <InputAdornment position="end">
              {inputProps.value ? (
                <Button {...getClearProps()} className={classes.searchClearButton}>
                  <ClearIcon />
                </Button>
              ) : (
                <SearchIcon className={classes.searchIcon} />
              )}
            </InputAdornment>
          )}
          {...inputProps}
          autoFocus={autoFocus}
          onFocus={handleSearchInputFocus}
          onClick={onSearchClickHandler}
        />
        {SearchManagertIcon && SearchManagertIcon}
      </div>

      <div ref={optionSelectContainerRef} className={classes.optionSelectContainer}>
        {showSearchManager ? SearchManagerComponent : renderOptions}
      </div>
    </div>
  );
};

const optionPropType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.any.isRequired,
});

OptionsListWithSearch.propTypes = {
  options: PropTypes.arrayOf(optionPropType).isRequired,
  fixedOptions: PropTypes.arrayOf(optionPropType).isRequired,
  staticOptions: PropTypes.arrayOf(optionPropType),
  groupedOptions: PropTypes.arrayOf(
    PropTypes.exact({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(optionPropType).isRequired,
      isDefault: PropTypes.bool,
    }),
  ),
  hideNoKnownMutation: PropTypes.bool,
  shouldShowSelectedGroup: PropTypes.bool,
  shouldScrollToTop: PropTypes.bool,
  isMultipleSelection: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  scrollToOptionHeight: PropTypes.number,
  values: PropTypes.array.isRequired,
  className: PropTypes.string,
  customCheckedOptionClass: PropTypes.string,
  customFixedOptionsClass: PropTypes.string,
  customFixedCheckedOptionsClass: PropTypes.string,
  searchFocusedClass: PropTypes.string,
  customOptionClass: PropTypes.string,
  searchInputRootClass: PropTypes.string,
  searchInputNotchedOutlineClass: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  isSearchableByOptionGroupName: PropTypes.bool,
  onSearchInputFocus: PropTypes.func,
  onSearchClick: PropTypes.func,
  onFilter: PropTypes.func,
  showSearchManager: PropTypes.bool,
  scrollToOption: PropTypes.any,
  showCheckBoxIcon: PropTypes.bool,
  SearchManagertIcon: PropTypes.element,
  SearchManagerComponent: PropTypes.element,
  autoFocus: PropTypes.bool,
};
